import calendarDe from '@fullcalendar/core/locales/de';
import calendarEs from '@fullcalendar/core/locales/es';
import calendarFr from '@fullcalendar/core/locales/fr';
import calendarIt from '@fullcalendar/core/locales/it';
import calendarPt from '@fullcalendar/core/locales/pt';
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import { register as timeagoRegister } from 'timeago.js';
import {
  de as timeagoDe,
  es as timeagoEs,
  fr as timeagoFr,
  it as timeagoIt,
  pt_BR as timeagoPt,
} from 'timeago.js/lib/lang';
import { setLocale as setYupLocale } from 'yup';
import { resources } from '~/generated/locales';

timeagoRegister('de', timeagoDe);
timeagoRegister('es', timeagoEs);
timeagoRegister('fr', timeagoFr);
timeagoRegister('it', timeagoIt);
timeagoRegister('pt', timeagoPt);

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    detection: { order: ['navigator'] },
    fallbackLng: 'en',
    interpolation: { escapeValue: false, prefix: '%{', suffix: '}' },
    missingKeyHandler: import.meta.env.DEV
      ? (lng, ns, key) => console.warn(`Missing translation: ${lng}.${ns}.${key}`)
      : undefined,
    pluralSeparator: '--',
    resources,
    returnEmptyString: false,
  });

// The language the UI is actually rendered in: an unsupported browser language falls back to English.
export const uiLanguage = (): string => i18n.resolvedLanguage ?? i18n.language;

// Function-form messages are resolved when a schema validates, so a bare `.required()` reads
// in the current language and follows a language change. Fields show these as helper text under
// a visible label, so the messages leave the field path out.
setYupLocale({
  array: {
    min: ({ min }) => i18n.t('errors.validation.array_min', { min }),
  },
  mixed: {
    defined: () => i18n.t('errors.validation.required'),
    notType: () => i18n.t('errors.validation.invalid'),
    required: () => i18n.t('errors.validation.required'),
  },
  number: {
    integer: () => i18n.t('errors.validation.integer'),
    max: ({ max }) => i18n.t('errors.validation.max', { max }),
    min: ({ min }) => i18n.t('errors.validation.min', { min }),
    positive: () => i18n.t('errors.validation.positive'),
  },
  string: {
    email: () => i18n.t('errors.validation.email'),
    matches: () => i18n.t('errors.validation.matches'),
    max: ({ max }) => i18n.t('errors.validation.string_max', { max }),
    min: ({ min }) => i18n.t('errors.validation.string_min', { min }),
    url: () => i18n.t('errors.validation.url'),
  },
});

export const getCalendarLocale = () => {
  if (i18n.language.startsWith('de')) return calendarDe;
  if (i18n.language.startsWith('es')) return calendarEs;
  if (i18n.language.startsWith('fr')) return calendarFr;
  if (i18n.language.startsWith('it')) return calendarIt;
  if (i18n.language.startsWith('pt')) return calendarPt;
};

// The server renders <html lang> for the signed-in user's locale; this keeps it right after
// a language change in the browser, so screen readers switch pronunciation with it (WCAG 3.1.1).
i18n.on('languageChanged', (language) => {
  document.documentElement.lang = language;
});

// Set language based on user preference (or reset to browser default if null)
export const setUserLocale = (locale: string | null | undefined): void => {
  if (locale) {
    void i18n.changeLanguage(locale);
  } else {
    void i18n.changeLanguage(navigator.language);
  }
};

export { i18n };
